import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AdminPatternPanel from '@/components/group/AdminPatternPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Pre-possession rooms: owners mark "affects me too" and keep a private note. No personal tickets.
export default function AffectsMeCard({ projectId, topicKey, isAdmin }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const url = `${API}/api/topics/${projectId}/${topicKey}/affected`;
  useEffect(() => {
    axios.get(url).then((r) => { setData(r.data); setNote(r.data.note); }).catch(() => setData(null));
  }, [url]);
  if (!data) return null;
  const save = (body, msg) => axios.put(url, body).then((r) => { setData(r.data); toast.success(msg); })
    .catch(() => toast.error('Could not save'));
  return (
    <section className="rounded-xl border border-green-300/70 bg-green-50/60 dark:bg-green-900/10 p-4 space-y-3" data-testid="affects-me-card">
      <div className="flex flex-wrap items-center gap-3">
        <Users className="h-5 w-5 text-green-700 shrink-0" />
        <p className="text-sm flex-1 min-w-[12rem]">
          <b data-testid="affects-me-total">{data.total}</b> {data.total === 1 ? 'owner says' : 'owners say'} this affects their flat.
        </p>
        <Button size="sm" variant={data.affected ? 'secondary' : 'default'} data-testid="affects-me-toggle"
          onClick={() => save({ affected: !data.affected }, data.affected ? 'Removed your mark' : 'Counted. Thank you')}>
          {data.affected ? 'Affects me (undo)' : 'Affects me too'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">FBC does not take up individual complaints. The total shows the group how widely an issue is shared, so members can act together.</p>
      <div className="space-y-1.5">
        <p className="text-xs font-medium flex items-center gap-1"><Lock className="h-3 w-3" />My private note (only you can see this)</p>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. demand letter dated..., amount, what was promised"
          className="text-sm bg-background" data-testid="affects-me-note" />
        <Button size="sm" variant="outline" className="h-7" disabled={note === data.note} onClick={() => save({ affected: data.affected, note }, 'Note saved')}
          data-testid="affects-me-note-save">Save note</Button>
      </div>
      {isAdmin && <AdminPatternPanel projectId={projectId} topicKey={topicKey} total={data.total} />}
    </section>
  );
}
